import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { TN_DEMO_FORCE_DEFAULT_OFF_ITEM_ID_SET } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/tennesseeNationalGuardLayers';
import config from 'constants/apiConfig';
import { externalLayersKeys } from 'hooks/externalLayer/queryKeys';
import type {
  ExternalLayer,
  ListLayersResponse,
} from 'hooks/externalLayer/types';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';

type UseEnsureTennesseeNationalGuardLayersParams = {
  groupGuid: string | null | undefined;
};

/** Shared locks so React Strict Mode remounts do not double-delete. */
const cleanupLocks = new Map<string, Promise<void>>();

async function removeTennesseeNationalGuardLayersForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const listResp = await axios.get<ListLayersResponse<ExternalLayer>>(
    config.apiGateway.external_layers,
    {
      params: {
        'filter[groupGuid]': groupGuid,
        per_page: '200',
      },
    }
  );

  const toDelete = (listResp.data.data ?? []).filter(
    (layer) =>
      !!layer.arcgisItemId &&
      TN_DEMO_FORCE_DEFAULT_OFF_ITEM_ID_SET.has(layer.arcgisItemId)
  );

  if (toDelete.length === 0) {
    return;
  }

  await Promise.all(
    toDelete.map((layer) =>
      axios.delete(`${config.apiGateway.external_layers}/${layer.id}`)
    )
  );

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: externalLayersKeys.all(),
    }),
    queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() }),
  ]);
}

/**
 * Remove previously auto-seeded Ting / FloodingBins / retired OSINT external
 * layers for the current group (hub and incident-scoped). Idempotent; no-op
 * once gone. Does not call PortalItem.
 */
export function useEnsureTennesseeNationalGuardLayers({
  groupGuid,
}: UseEnsureTennesseeNationalGuardLayersParams) {
  const queryClient = useQueryClient();
  const completedGroupRef = useRef<string | null>(null);

  useEffect(() => {
    if (!groupGuid) {
      return;
    }

    if (completedGroupRef.current === groupGuid) {
      return;
    }

    let cancelled = false;
    let cleanupPromise = cleanupLocks.get(groupGuid);
    if (!cleanupPromise) {
      cleanupPromise = removeTennesseeNationalGuardLayersForGroup({
        groupGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[TN National Guard] Failed to remove seeded ArcGIS layers',
            error
          );
          throw error;
        })
        .finally(() => {
          cleanupLocks.delete(groupGuid);
        });
      cleanupLocks.set(groupGuid, cleanupPromise);
    }

    void cleanupPromise
      .then(() => {
        if (!cancelled) {
          completedGroupRef.current = groupGuid;
        }
      })
      .catch(() => {
        // Error already logged; allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [groupGuid, queryClient]);
}
